import React from "react"
import { Box, Flex, Image, PseudoBox, Text } from "@chakra-ui/core"
import { bp } from "../../utils/MediaQueries"
import {
  ChatIcon,
  WidgetCloseButton,
  openZendeskChat,
  useFittingConsultation,
} from "./shared"
import { HorizontalFittingCard } from "./HorizontalWidget"

export { default as HorizontalFittingConsultationWidget } from "./HorizontalWidget"

const WIDGET_SCALE = 0.82
const MOBILE_WIDGET_SCALE = 0.78
const HELP_BUTTON_CLEARANCE = "5.5rem"

type FittingOptionProps = {
  title: string
  subtitle: string
  icon: React.ReactNode
  onClick: () => void
}

const FittingOption = ({
  title,
  subtitle,
  icon,
  onClick,
}: FittingOptionProps) => (
  <PseudoBox
    as="button"
    w="100%"
    h={bp("58px", "74px")}
    px={bp("10px", "14px")}
    py={bp("8px", "12px")}
    bg="#f4f8f9"
    border="1px solid #e6ebee"
    borderRadius={bp("10px", "14px")}
    textAlign="left"
    cursor="pointer"
    transition="border-color 0.2s ease, box-shadow 0.2s ease"
    _hover={{
      borderColor: "primary",
      boxShadow: "0 4px 12px rgba(0, 196, 209, 0.12)",
    }}
    onClick={onClick}
  >
    <Flex align="center" h="100%">
      <Flex
        w={bp("48px", "66px")}
        h={bp("38px", "50px")}
        borderRadius="11px"
        bg="rgba(0, 196, 209, 0.12)"
        align="center"
        justify="center"
        flexShrink={0}
        transform={bp("scale(0.85)", "none")}
      >
        {icon}
      </Flex>
      <Box flex="1" ml={bp("10px", "14px")}>
        <Text
          fontSize={bp("15px", "18px")}
          fontWeight="600"
          color="#14181b"
          lineHeight="1.2"
        >
          {title}
        </Text>
        <Text fontSize={bp("11px", "12px")} color="#5a6a72" mt="2px">
          {subtitle}
        </Text>
      </Box>
      <Text fontSize={bp("16px", "18px")} color="primary" flexShrink={0}>
        →
      </Text>
    </Flex>
  </PseudoBox>
)

const FittingConsultationWidget = () => {
  const { isVisible, dismissWidget, handleFittingSelect } =
    useFittingConsultation()

  if (!isVisible) return null

  return (
    <>
      <Box
        position="fixed"
        left={bp("0.75rem", "1.25rem")}
        right="auto"
        bottom={HELP_BUTTON_CLEARANCE}
        zIndex={1002}
        w={bp("300px", "380px")}
        maxW={bp("300px", "380px")}
        maxH={`calc(100vh - ${HELP_BUTTON_CLEARANCE} - 2rem)`}
        transform={bp(
          `scale(${MOBILE_WIDGET_SCALE})`,
          `scale(${WIDGET_SCALE})`
        )}
        transformOrigin="bottom left"
        overflowY="auto"
        pointerEvents="auto"
      >
        <Box display={bp("block", "none")}>
          <HorizontalFittingCard
            dismissWidget={dismissWidget}
            handleFittingSelect={handleFittingSelect}
          />
        </Box>
        <Box display={bp("none", "block")}>
          <Box
            bg="white"
            borderRadius={bp("12px", "14px")}
            border="1px solid #e6ebee"
            boxShadow="0 12px 40px rgba(18, 25, 35, 0.12)"
            overflow="hidden"
          >
            <Box
              bg="primary"
              px={bp("1rem", "28px")}
              pt={bp("10px", "14px")}
              pb={bp("12px", "18px")}
              position="relative"
              borderTopLeftRadius={bp("12px", "14px")}
              borderTopRightRadius={bp("12px", "14px")}
            >
              <Text
                color="white"
                fontSize={bp("9px", "11.5px")}
                fontWeight="700"
                letterSpacing={bp("1.2px", "1.7px")}
                textAlign="center"
                lineHeight="1.4"
              >
                GET YOUR PERSONAL FITTING
                <br />
                CONSULTATION
              </Text>
              <WidgetCloseButton onClick={dismissWidget} />
            </Box>

            <Box
              px={bp("1rem", "28px")}
              pt={bp("1rem", "28px")}
              pb={bp("1rem", "24px")}
            >
              <Text
                fontSize={bp("20px", "33px")}
                fontWeight="700"
                color="#14181b"
                letterSpacing="-0.6px"
                lineHeight="1.15"
              >
                No chatbots.
                <br />
                No automated
                <br />
                quizzes.
              </Text>

              <Text
                fontSize={bp("12px", "15px")}
                color="#5a6a72"
                mt={bp("12px", "18px")}
                lineHeight="1.5"
              >
                Just honest advice from{" "}
                <Text as="span" color="primary" fontWeight="700">
                  Dustin
                </Text>{" "}
                — our real bike expert.
              </Text>

              <Flex
                mt={bp("12px", "20px")}
                align="center"
                px={bp("10px", "14px")}
                py={bp("8px", "10px")}
                border="1px solid #e6ebee"
                borderRadius="30px"
                bg="white"
              >
                <Image
                  src="/images/Dustin.jpg"
                  alt="Dustin, Head Fit Specialist"
                  w={bp("36px", "44px")}
                  h={bp("36px", "44px")}
                  borderRadius="50%"
                  objectFit="cover"
                  flexShrink={0}
                />
                <Box ml={bp("10px", "14px")}>
                  <Text
                    fontSize={bp("12px", "14px")}
                    fontWeight="700"
                    color="#14181b"
                  >
                    Dustin
                  </Text>
                  <Text fontSize={bp("10px", "11px")} color="#5a6a72">
                    Head Fit Specialist · sixthreezero
                  </Text>
                </Box>
              </Flex>

              <Text
                fontSize={bp("10px", "11px")}
                fontWeight="700"
                color="#5a6a72"
                letterSpacing={bp("1.5px", "2px")}
                mt={bp("16px", "28px")}
                mb={bp("8px", "10px")}
              >
                WHAT ARE YOU HERE TO FIT?
              </Text>

              <Flex direction="column" gridGap={bp("8px", "10px")}>
                <FittingOption
                  title="E-Trike Fitting"
                  subtitle="Three-wheel stability"
                  icon={
                    <Image
                      src="/images/etrike.png"
                      alt="E-Trike"
                      w="85%"
                      h="85%"
                      objectFit="contain"
                    />
                  }
                  onClick={() => handleFittingSelect("E-Trike Fitting")}
                />
                <FittingOption
                  title="E-Bike Fitting"
                  subtitle="Two-wheel classic"
                  icon={
                    <Image
                      src="/images/ebike.png"
                      alt="E-Bike"
                      w="85%"
                      h="85%"
                      objectFit="contain"
                    />
                  }
                  onClick={() => handleFittingSelect("E-Bike Fitting")}
                />
              </Flex>

              <Box
                borderTop="1px solid #e6ebee"
                mt={bp("16px", "24px")}
                pt={bp("12px", "16px")}
              >
                <Flex
                  as="button"
                  align="center"
                  justify="center"
                  w="100%"
                  cursor="pointer"
                  bg="transparent"
                  border="none"
                  onClick={openZendeskChat}
                >
                  <ChatIcon />
                  <Text
                    fontSize={bp("11px", "13px")}
                    fontWeight="600"
                    color="#14181b"
                    ml="10px"
                  >
                    Real Advice. Real People.
                  </Text>
                </Flex>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default FittingConsultationWidget
